import React from 'react';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import Tooltip from "@mui/material/Tooltip";
import FlagIcon from '@mui/icons-material/Flag';
import toast from 'react-hot-toast';
import { Box, IconButton } from "@mui/material";

import { commonSwal } from '../../../../../components/swalAlert/swalAlert';
import { markDocumentAsConcern, updateApproveDocument, updateRejectDocument } from '../../../../documents/view/service';
import DeletedDocuments from '../../../../documents/deleted/DeletedDocuments';
import History from '../../../../documents/history/History';
import SetExpiryDate from '../../../../documents/view/components/SetExpiryDate';

const ActionBtnList = ({ ftm_id, document_type, selectedDocument, handleRefresh, acceptCheckList, documentIsExpired, approveNewDocument, closeModal }) => {
    const isPersonal = document_type === "personal";

    const documentId = isPersonal ? selectedDocument?.ftm_personal_document_id : selectedDocument?.ftm_document_id;
    const documentTypeId = isPersonal ? selectedDocument?.ftm_personal_document_type_id : selectedDocument?.ftm_documents_types_id;
    const expiryDate = isPersonal ? selectedDocument?.ftm_personal_document_expiry_date : selectedDocument?.ftm_document_expiry_date;
    const comment = isPersonal ? selectedDocument?.ftm_personal_document_comment : selectedDocument?.ftm_document_comment;
    const hasNoDocsCerts = !selectedDocument?.ftm_personal_document_files?.length && !selectedDocument?.ftm_document_files?.length;
    const isApprovedDocsCerts = isPersonal ? selectedDocument?.ftm_personal_document_is_approve : selectedDocument?.ftm_document_is_approve;
    const isRejectedDocsCerts = isPersonal ? selectedDocument?.ftm_personal_document_is_rejected : selectedDocument?.ftm_document_is_rejected;
    const markedAsConcernDocsCerts = isPersonal ? selectedDocument?.ftm_personal_document_marked_as_concern : selectedDocument?.ftm_document_marked_as_concern;

    const approveFtmDocument = ({ document_id }) => {
        commonSwal({
            notificationType: isPersonal ? 'APPROVE_FTM_DOCUMENT' : 'APPROVE_FTM_CERTIFICATE',
            preConfirm: async () => {
                const response = await updateApproveDocument({ document_id, document_type, approve_new_document: approveNewDocument });
                return response;
            },
            isConfirmed: (result) => {
                if (result.isConfirmed) {
                    if (result?.value?.status === "Success") {
                        handleRefresh();
                        closeModal()
                        toast.success(result?.value?.message);
                    } else {
                        toast.error(result?.value?.message);
                    }
                }
            }
        });
    }

    const rejectFtmDocument = ({ document_id }) => {
        commonSwal({
            notificationType: isPersonal ? 'REJECT_FTM_DOCUMENT' : 'REJECT_FTM_CERTIFICATE',
            preConfirm: async () => {
                const response = await updateRejectDocument({ document_id, document_type, ftm_id });
                return response;
            },
            isConfirmed: (result) => {
                if (result.isConfirmed) {
                    if (result?.value?.status === "Success") {
                        handleRefresh();
                        toast.success(result?.value?.message);
                    } else {
                        toast.error(result?.value?.message);
                    }
                }
            }
        });
    };

    const ftmDocumentMarkAsConcern = ({ document_id }) => {
        commonSwal({
            notificationType: isPersonal ? 'MARK_CONCERN_FTM_DOCUMENT' : 'MARK_CONCERN_FTM_CERTIFICATE',
            preConfirm: async () => {
                const response = await markDocumentAsConcern({ document_id, document_type });
                return response;
            },
            isConfirmed: (result) => {
                if (result.isConfirmed) {
                    if (result?.value?.status === "Success") {
                        handleRefresh();
                        toast.success(result?.value?.message);
                    } else {
                        toast.error(result?.value?.message);
                    }
                }
            }
        });
    };

    const actions = [
        {
            name: "EXPIRED",
            icon: <ScheduleSendIcon />,
            component: ({ action, index }) => (
                <SetExpiryDate
                    listItem={actions[index]}
                    selectedDocument={{
                        document_id: documentId,
                        expiry_date: expiryDate,
                        comment: comment,
                        document_type: document_type,
                    }}
                    document_type={document_type}
                    refresh={handleRefresh}
                />
            ),
        },
        {
            name: "APPROVE",
            component: ({ action }) => (
                <Tooltip
                    title={
                        isApprovedDocsCerts
                            ? "Approved"
                            : !hasNoDocsCerts
                                ? "Approve"
                                : "No documents available"
                    }
                    arrow
                >
                    <IconButton
                        onClick={() => {
                            if (isApprovedDocsCerts) return;
                            action();
                        }}
                        color={isApprovedDocsCerts ? "success" : "default"}
                        disabled={hasNoDocsCerts || !acceptCheckList || documentIsExpired}
                        style={{ cursor: isApprovedDocsCerts ? 'context-menu' : 'pointer' }}
                    >
                        <ThumbUpAltIcon />
                    </IconButton>
                </Tooltip>

            ),
            action: () => approveFtmDocument({
                document_id: documentId
            }),
        },
        {
            name: "REJECT",
            component: ({ action }) => (
                <Tooltip
                    title={isRejectedDocsCerts ? "Rejected" : (!hasNoDocsCerts) ? "Reject" : null}
                    arrow
                >
                    <IconButton
                        onClick={() => {
                            if (isRejectedDocsCerts) return;
                            action();
                        }}
                        color={isRejectedDocsCerts ? "error" : "default"}
                        disabled={hasNoDocsCerts}
                        style={{ cursor: isRejectedDocsCerts ? 'context-menu' : 'pointer' }}
                    >
                        <ThumbDownAltIcon />
                    </IconButton>
                </Tooltip>
            ),
            action: () => rejectFtmDocument({
                document_id: documentId
            }),
        },
        {
            name: "MARK AS CONCERN",
            component: ({ action }) => (
                <Tooltip
                    title={markedAsConcernDocsCerts ? "Marked as a concern" : (!hasNoDocsCerts) ? "Mark as a concern" : null}
                    arrow
                >
                    <IconButton
                        onClick={() => {
                            if (markedAsConcernDocsCerts) return;
                            action();
                        }}
                        color={markedAsConcernDocsCerts ? "warning" : "default"}
                        disabled={hasNoDocsCerts}
                        style={{ cursor: markedAsConcernDocsCerts ? 'context-menu' : 'pointer' }}
                    >
                        <FlagIcon />
                    </IconButton>
                </Tooltip>
            ),
            action: () => ftmDocumentMarkAsConcern({
                document_id: documentId
            }),
        }
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                p: 1,           // optional padding
            }}
        >
            {/* LEFT group: action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {actions.map(({ component, action, ...rest }, index) =>
                    React.createElement(component, { key: index, action, index, ...rest })
                )}
            </Box>

            {/* RIGHT group: DeletedDocuments + History */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeletedDocuments
                    ftm_id={ftm_id}
                    document_id={documentId}
                    document_type={document_type}
                />
                <History
                    ftm_id={ftm_id}
                    document_type_id={documentTypeId}
                    document_type={document_type}
                />
            </Box>
        </Box>
    );
};

export default ActionBtnList;